import type { NextWebVitalsMetric } from 'next/app';
import Router from 'next/router';
import {
  BrowserMetrics,
  createUniversalReporter,
  createSemanticMetrics,
  SemanticAggregatorReporter,
} from '@spotify-internal/semantic-metrics';

const reporter = process.browser
  ? // client-side reporter
    // https://backstage.spotify.net/docs/semantic-metrics-js/reporters/#eventsenderreporter
    createUniversalReporter()
  : // server-side reporter
    // https://backstage.spotify.net/docs/semantic-metrics-js/reporters/#semanticaggregatorreporter
    new SemanticAggregatorReporter();

export const sendMetric = process.env.SPOTIFY_COMPONENT_ID
  ? createSemanticMetrics({
      dev: process.env.NODE_ENV !== 'production',
      key: process.env.SPOTIFY_COMPONENT_ID,
      reporter: [reporter],
    }).sendMetric
  : async () => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('process.env.SPOTIFY_COMPONENT_ID not set');
      }
    };

/**
 * Calculate the rating for a Web Vital based on official thresholds
 * @see https://web.dev/vitals/
 */
function getWebVitalRating(
  name: string,
  value: number,
): 'good' | 'needs-improvement' | 'poor' {
  switch (name) {
    case 'LCP':
      // Largest Contentful Paint
      if (value <= 2500) return 'good';
      if (value <= 4000) return 'needs-improvement';
      return 'poor';

    case 'FCP':
      // First Contentful Paint
      if (value <= 1800) return 'good';
      if (value <= 3000) return 'needs-improvement';
      return 'poor';

    case 'CLS':
      // Cumulative Layout Shift (note: CLS is unitless)
      if (value <= 0.1) return 'good';
      if (value <= 0.25) return 'needs-improvement';
      return 'poor';

    case 'FID':
      // First Input Delay
      if (value <= 100) return 'good';
      if (value <= 300) return 'needs-improvement';
      return 'poor';

    case 'TTFB':
      // Time to First Byte
      if (value <= 800) return 'good';
      if (value <= 1800) return 'needs-improvement';
      return 'poor';

    case 'INP':
      // Interaction to Next Paint
      if (value <= 200) return 'good';
      if (value <= 500) return 'needs-improvement';
      return 'poor';

    default:
      // For Next.js custom metrics, use general timing thresholds
      if (value <= 1000) return 'good';
      if (value <= 3000) return 'needs-improvement';
      return 'poor';
  }
}

/**
 * Report web vitals and custom next.js metrics to semantic metrics.
 * @see https://backstage.spotify.net/docs/semantic-metrics-js/metrics/#web-vitals
 */
export function reportWebVitals(metric: NextWebVitalsMetric) {
  // Use the route pattern for metrics tagging (for grouping in Grafana)
  const route = Router.router?.route ?? 'unknown';

  // Use the actual URL path for logging (more readable for developers)
  const actualPath =
    Router.router?.asPath ??
    (typeof window !== 'undefined' ? window.location.pathname : 'unknown');

  // Console log in development
  if (process.env.NODE_ENV === 'development') {
    const value = metric.value.toFixed(2);
    const rating = getWebVitalRating(metric.name, metric.value);

    let emoji = '📊';
    if (rating === 'good') {
      emoji = '✅';
    } else if (rating === 'needs-improvement') {
      emoji = '⚠️';
    } else if (rating === 'poor') {
      emoji = '❌';
    }

    // Format value with appropriate unit
    const unit = metric.name === 'CLS' ? '' : 'ms';
    const formattedValue =
      metric.name === 'CLS' ? metric.value.toFixed(3) : value;

    // eslint-disable-next-line no-console
    console.log(
      `%c[Web Vitals] ${emoji} ${metric.name}`,
      'font-weight: bold; color: #1db954',
      `\n  Value: ${formattedValue}${unit}`,
      `\n  Rating: ${rating}`,
      `\n  URL: ${actualPath}`,
      `\n  Route Pattern: ${route}`,
      `\n  ID: ${metric.id}`,
    );
  }

  // Send the standard Web Vitals metric
  sendMetric(
    BrowserMetrics.getWebVitalsMetric(metric, {
      route,
      path: actualPath,
    }),
  );

  // Report additional custom metrics on page load complete
  if (metric.name === 'FCP' && typeof window !== 'undefined') {
    // Use setTimeout to ensure all metrics are available
    setTimeout(() => {
      reportAdditionalMetrics(route, actualPath);
    }, 0);
  }
}

/**
 * Report additional performance metrics beyond standard Web Vitals
 */
function reportAdditionalMetrics(route: string, actualPath: string) {
  if (typeof window === 'undefined' || !window.performance) return;

  const isDev = process.env.NODE_ENV === 'development';
  const metricsToLog: Array<{ name: string; value: number }> = [];

  try {
    const navigation = performance.getEntriesByType(
      'navigation',
    )[0] as PerformanceNavigationTiming;

    if (navigation) {
      // DOM Content Loaded Time
      const domContentLoaded =
        navigation.domContentLoadedEventEnd -
        navigation.domContentLoadedEventStart;
      if (domContentLoaded > 0) {
        sendMetric({
          what: 'dom_content_loaded',
          metric_type: 'gauge',
          value: domContentLoaded,
          tags: { route, path: actualPath },
        });
        if (isDev)
          metricsToLog.push({
            name: 'DOM Content Loaded',
            value: domContentLoaded,
          });
      }

      // DOM Interactive Time (when DOM is ready)
      const domInteractive = navigation.domInteractive - navigation.fetchStart;
      if (domInteractive > 0) {
        sendMetric({
          what: 'dom_interactive',
          metric_type: 'gauge',
          value: domInteractive,
          tags: { route, path: actualPath },
        });
        if (isDev)
          metricsToLog.push({ name: 'DOM Interactive', value: domInteractive });
      }

      // DOM Complete Time (page fully loaded)
      const domComplete = navigation.domComplete - navigation.fetchStart;
      if (domComplete > 0) {
        sendMetric({
          what: 'dom_complete',
          metric_type: 'gauge',
          value: domComplete,
          tags: { route, path: actualPath },
        });
        if (isDev)
          metricsToLog.push({ name: 'DOM Complete', value: domComplete });
      }

      // Full Page Load Time
      const pageLoadTime = navigation.loadEventEnd - navigation.fetchStart;
      if (pageLoadTime > 0) {
        sendMetric({
          what: 'page_load_time',
          metric_type: 'gauge',
          value: pageLoadTime,
          tags: { route, path: actualPath },
        });
        if (isDev)
          metricsToLog.push({ name: 'Page Load Time', value: pageLoadTime });
      }

      // DNS Lookup Time
      const dnsTime = navigation.domainLookupEnd - navigation.domainLookupStart;
      if (dnsTime > 0) {
        sendMetric({
          what: 'dns_lookup_time',
          metric_type: 'gauge',
          value: dnsTime,
          tags: { route, path: actualPath },
        });
        if (isDev) metricsToLog.push({ name: 'DNS Lookup', value: dnsTime });
      }

      // TCP Connection Time
      const tcpTime = navigation.connectEnd - navigation.connectStart;
      if (tcpTime > 0) {
        sendMetric({
          what: 'tcp_connection_time',
          metric_type: 'gauge',
          value: tcpTime,
          tags: { route, path: actualPath },
        });
        if (isDev)
          metricsToLog.push({ name: 'TCP Connection', value: tcpTime });
      }

      // Request Time (server processing)
      const requestTime = navigation.responseStart - navigation.requestStart;
      if (requestTime > 0) {
        sendMetric({
          what: 'server_response_time',
          metric_type: 'gauge',
          value: requestTime,
          tags: { route, path: actualPath },
        });
        if (isDev)
          metricsToLog.push({ name: 'Server Response', value: requestTime });
      }

      // Response Download Time
      const downloadTime = navigation.responseEnd - navigation.responseStart;
      if (downloadTime > 0) {
        sendMetric({
          what: 'response_download_time',
          metric_type: 'gauge',
          value: downloadTime,
          tags: { route, path: actualPath },
        });
        if (isDev)
          metricsToLog.push({ name: 'Response Download', value: downloadTime });
      }
    }

    // Report Resource Timing (summary stats)
    const resources = performance.getEntriesByType(
      'resource',
    ) as PerformanceResourceTiming[];
    if (resources.length > 0) {
      // Count resources by type
      const resourceCounts: Record<string, number> = {};
      const resourceDurations: Record<string, number[]> = {};

      resources.forEach(resource => {
        const type = resource.initiatorType || 'other';
        resourceCounts[type] = (resourceCounts[type] || 0) + 1;

        if (!resourceDurations[type]) {
          resourceDurations[type] = [];
        }
        resourceDurations[type].push(resource.duration);
      });

      // Report counts and average durations
      Object.entries(resourceCounts).forEach(([type, count]) => {
        sendMetric({
          what: 'resource_count',
          metric_type: 'gauge',
          value: count,
          tags: { route, path: actualPath, resource_type: type },
        });

        const durations = resourceDurations[type];
        const avgDuration =
          durations.reduce((a, b) => a + b, 0) / durations.length;
        sendMetric({
          what: 'resource_avg_duration',
          metric_type: 'gauge',
          value: avgDuration,
          tags: { route, path: actualPath, resource_type: type },
        });
      });

      // Log resource summary in development
      if (isDev) {
        // eslint-disable-next-line no-console
        console.log(
          '%c[Resources]',
          'font-weight: bold; color: #1db954',
          '\n  Total:',
          resources.length,
          '\n  By type:',
          resourceCounts,
        );
      }
    }

    // Report Long Tasks (if available)
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver(list => {
          for (const entry of list.getEntries()) {
            sendMetric({
              what: 'long_task',
              metric_type: 'gauge',
              value: entry.duration,
              tags: { route, path: actualPath },
            });

            // Log long tasks in development (these are performance issues)
            if (isDev) {
              // eslint-disable-next-line no-console
              console.warn(
                '%c[Long Task] ⚠️',
                'font-weight: bold; color: #ff9800',
                `\n  Duration: ${entry.duration.toFixed(2)}ms`,
                '\n  This task blocked the main thread and may cause jank',
              );
            }
          }
        });
        longTaskObserver.observe({ type: 'longtask', buffered: true });
      } catch (e) {
        // Long Task API not supported
      }
    }

    // Log all navigation timing metrics in development
    if (isDev && metricsToLog.length > 0) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `%c[Additional Metrics] 📊 ${actualPath}`,
        'font-weight: bold; color: #1db954',
      );
      metricsToLog.forEach(({ name, value }) => {
        // eslint-disable-next-line no-console
        console.log(`  ${name}: ${value.toFixed(2)}ms`);
      });
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  } catch (error) {
    // Silently fail - don't break the page if metrics fail
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Failed to report additional metrics:', error);
    }
  }
}
